import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { getRankInfo, RANK_COLORS } from '../rank';

const API = process.env.REACT_APP_API_URL || '';

// ─── Utilities ────────────────────────────────────────────────────────────────
function fmt(n)  { return n == null ? '—' : Number(n).toLocaleString(); }
function pct(n)  { return n == null ? '—' : `${n}%`; }
function ago(ts) {
  if (!ts) return '—';
  const diff = Date.now() - new Date(ts);
  if (diff < 60000)    return 'just now';
  if (diff < 3600000)  return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return new Date(ts).toLocaleDateString();
}

// ─── Rank badge ───────────────────────────────────────────────────────────────
function RankBadge({ rating }) {
  const info = getRankInfo(rating ?? 0);
  return (
    <span style={{
      background: info.color + '20',
      color: info.color,
      border: `1px solid ${info.color}50`,
      borderRadius: 4,
      padding: '1px 8px',
      fontSize: 11,
      fontWeight: 700,
      whiteSpace: 'nowrap',
      letterSpacing: '.01em',
    }}>
      {info.label}
    </span>
  );
}

// ─── Stat card ────────────────────────────────────────────────────────────────
function StatCard({ label, value, sub, accent }) {
  return (
    <div style={{
      background: 'var(--cb-white)',
      border: '1px solid var(--cb-gray-200)',
      borderRadius: 'var(--radius-lg)',
      boxShadow: 'var(--shadow-sm)',
      padding: '20px 22px',
      flex: '1 1 130px',
    }}>
      <div style={{
        fontSize: 28,
        fontWeight: 800,
        color: accent || 'var(--cb-navy-text)',
        lineHeight: 1.1,
      }}>
        {fmt(value)}
      </div>
      <div style={{ fontSize: 12, color: 'var(--cb-gray-500)', marginTop: 4, fontWeight: 500 }}>
        {label}
      </div>
      {sub != null && (
        <div style={{ fontSize: 11, color: 'var(--cb-gray-500)', marginTop: 3 }}>{sub}</div>
      )}
    </div>
  );
}

// ─── Progress bar ─────────────────────────────────────────────────────────────
function Bar({ value, max, color }) {
  const w = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div style={{
      background: 'var(--cb-gray-200)',
      borderRadius: 4,
      height: 7,
      width: '100%',
      overflow: 'hidden',
    }}>
      <div style={{
        width: `${w}%`,
        height: '100%',
        background: color || 'var(--cb-blue)',
        borderRadius: 4,
        transition: 'width .3s ease',
      }} />
    </div>
  );
}

// ─── Section card wrapper ─────────────────────────────────────────────────────
function SectionCard({ title, children, style }) {
  return (
    <div style={{
      background: 'var(--cb-white)',
      border: '1px solid var(--cb-gray-200)',
      borderRadius: 'var(--radius-lg)',
      boxShadow: 'var(--shadow-sm)',
      overflow: 'hidden',
      ...style,
    }}>
      {title && (
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid var(--cb-gray-200)',
          fontSize: 14,
          fontWeight: 700,
          color: 'var(--cb-navy-text)',
          letterSpacing: '-.01em',
        }}>
          {title}
        </div>
      )}
      <div style={{ padding: 20 }}>
        {children}
      </div>
    </div>
  );
}

// ─── Data table ───────────────────────────────────────────────────────────────
function Table({ cols, rows, keyField = 'id' }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ borderBottom: '2px solid var(--cb-gray-200)' }}>
            {cols.map(c => (
              <th key={c.key} style={{
                textAlign: c.right ? 'right' : 'left',
                padding: '7px 12px',
                color: 'var(--cb-gray-500)',
                fontWeight: 600,
                fontSize: 11,
                textTransform: 'uppercase',
                letterSpacing: '.05em',
                whiteSpace: 'nowrap',
              }}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={row[keyField] ?? i}
              style={{ borderBottom: '1px solid var(--cb-gray-200)' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--cb-gray-100)'}
              onMouseLeave={e => e.currentTarget.style.background = ''}
            >
              {cols.map(c => (
                <td key={c.key} style={{
                  padding: '10px 12px',
                  textAlign: c.right ? 'right' : 'left',
                  color: 'var(--cb-gray-900)',
                  verticalAlign: 'middle',
                }}>
                  {c.render ? c.render(row[c.key], row) : (row[c.key] ?? '—')}
                </td>
              ))}
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={cols.length} style={{
                padding: '32px 12px',
                textAlign: 'center',
                color: 'var(--cb-gray-500)',
                fontSize: 14,
              }}>
                No data
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ─── Pagination ───────────────────────────────────────────────────────────────
function Pagination({ page, total, limit, onPage }) {
  const pages = Math.max(1, Math.ceil(total / limit));
  if (pages <= 1) return null;
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 14, flexWrap: 'wrap' }}>
      <button
        className="btn btn-ghost btn-sm"
        disabled={page <= 1}
        onClick={() => onPage(page - 1)}
      >
        Previous
      </button>
      <span style={{ fontSize: 13, color: 'var(--cb-gray-500)' }}>
        Page {page} of {pages} &nbsp;&middot;&nbsp; {fmt(total)} total
      </span>
      <button
        className="btn btn-ghost btn-sm"
        disabled={page >= pages}
        onClick={() => onPage(page + 1)}
      >
        Next
      </button>
    </div>
  );
}

// ─── Loading / error states ───────────────────────────────────────────────────
function Loading() {
  return (
    <div style={{ padding: 48, textAlign: 'center', color: 'var(--cb-gray-500)', fontSize: 14 }}>
      Loading…
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB: OVERVIEW
// ═══════════════════════════════════════════════════════════════════════════════
function OverviewTab() {
  const [data, setData]   = useState(null);
  const [chart, setChart] = useState([]);
  const [live, setLive]   = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [ov, ch, lv] = await Promise.all([
        axios.get(`${API}/api/admin/overview`),
        axios.get(`${API}/api/admin/signups-chart?days=14`),
        axios.get(`${API}/api/admin/live`),
      ]);
      setData(ov.data);
      setChart(ch.data);
      setLive(lv.data);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Loading />;
  if (!data) return <div style={{ padding: 32, color: 'var(--cb-gray-500)' }}>Failed to load.</div>;

  const maxSignups = Math.max(1, ...chart.map(r => parseInt(r.count)));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Stat grid */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
        <StatCard label="Real Players"   value={data.totalUsers}   accent="var(--cb-blue)" />
        <StatCard label="Bot Accounts"   value={data.botUsers}     accent="var(--cb-gray-500)" />
        <StatCard label="Online Now"     value={data.onlineUsers}  accent="var(--cb-green)" />
        <StatCard label="In Queue"       value={data.queueSize}    accent="var(--cb-yellow)" />
        <StatCard label="Active Games"   value={data.activeGames}  accent="var(--cb-red)" />
        <StatCard label="Matches Today"  value={data.todayMatches} />
        <StatCard label="Matches (7d)"   value={data.weekMatches} />
        <StatCard label="Total Matches"  value={data.totalMatches} />
        <StatCard label="Signups Today"  value={data.todaySignups} accent="var(--cb-green)" />
        <StatCard label="Signups (7d)"   value={data.weekSignups}  accent="var(--cb-green)" />
        <StatCard label="Avg Rating"     value={data.avgRating} />
        <StatCard label="Top Rating"     value={data.topRating}    accent="var(--cb-red)" />
      </div>

      {/* Signup chart */}
      <SectionCard title="New Players — Last 14 Days">
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 5, height: 96, paddingBottom: 28, position: 'relative' }}>
          {chart.map((r, i) => {
            const h = Math.round((parseInt(r.count) / maxSignups) * 72);
            return (
              <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, height: '100%', justifyContent: 'flex-end' }}>
                <div
                  title={`${new Date(r.day).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}: ${r.count} signups`}
                  style={{
                    width: '100%',
                    height: Math.max(2, h),
                    background: 'var(--cb-blue)',
                    borderRadius: '3px 3px 0 0',
                    opacity: 0.85,
                  }}
                />
                <div style={{
                  fontSize: 9,
                  color: 'var(--cb-gray-500)',
                  position: 'absolute',
                  bottom: 0,
                  transform: 'rotate(-40deg)',
                  whiteSpace: 'nowrap',
                  transformOrigin: 'top center',
                }}>
                  {new Date(r.day).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                </div>
              </div>
            );
          })}
          {chart.length === 0 && (
            <div style={{ color: 'var(--cb-gray-500)', fontSize: 13, alignSelf: 'center' }}>No data</div>
          )}
        </div>
      </SectionCard>

      {/* Live state */}
      {live && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <SectionCard title={`Active Games (${live.games.length})`}>
            {live.games.length === 0
              ? <div style={{ color: 'var(--cb-gray-500)', fontSize: 13 }}>No games in progress</div>
              : live.games.map((g, i) => (
                <div key={i} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '8px 12px', background: 'var(--cb-gray-100)',
                  borderRadius: 'var(--radius)', marginBottom: 6, fontSize: 13,
                }}>
                  <span>
                    <strong>{g.p1.name}</strong> {g.p1.score} — {g.p2.score} <strong>{g.p2.name}</strong>
                  </span>
                  <span style={{ color: 'var(--cb-gray-500)', fontSize: 11 }}>{ago(g.startedAt)}</span>
                </div>
              ))
            }
          </SectionCard>
          <SectionCard title={`Matchmaking Queue (${live.queue.length})`}>
            {live.queue.length === 0
              ? <div style={{ color: 'var(--cb-gray-500)', fontSize: 13 }}>Queue is empty</div>
              : live.queue.map((q, i) => (
                <div key={i} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '6px 0', borderBottom: '1px solid var(--cb-gray-200)', fontSize: 13,
                }}>
                  <span style={{ color: 'var(--cb-gray-900)' }}>{q.name}</span>
                  <RankBadge rating={q.rating} />
                </div>
              ))
            }
          </SectionCard>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// SHARED RANK TIER DATA
// ═══════════════════════════════════════════════════════════════════════════════
const TIER_ORDER = [
  'Bronze I','Bronze II','Bronze III',
  'Silver I','Silver II','Silver III',
  'Gold I','Gold II','Gold III',
  'Platinum I','Platinum II','Platinum III',
  'Diamond I','Diamond II','Diamond III',
  'Ascendant I','Ascendant II','Ascendant III',
  'Master I','Master II','Master III',
  'Grandmaster',
];
const TIER_FAMILY = {
  'Bronze I':'Bronze','Bronze II':'Bronze','Bronze III':'Bronze',
  'Silver I':'Silver','Silver II':'Silver','Silver III':'Silver',
  'Gold I':'Gold','Gold II':'Gold','Gold III':'Gold',
  'Platinum I':'Platinum','Platinum II':'Platinum','Platinum III':'Platinum',
  'Diamond I':'Diamond','Diamond II':'Diamond','Diamond III':'Diamond',
  'Ascendant I':'Ascendant','Ascendant II':'Ascendant','Ascendant III':'Ascendant',
  'Master I':'Master','Master II':'Master','Master III':'Master',
  'Grandmaster':'Grandmaster',
};
const FAMILIES = ['Bronze','Silver','Gold','Platinum','Diamond','Ascendant','Master','Grandmaster'];

// ═══════════════════════════════════════════════════════════════════════════════
// TAB: RANK DISTRIBUTION
// ═══════════════════════════════════════════════════════════════════════════════
function RankDistributionTab() {
  const [rows, setRows]   = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/api/admin/rank-distribution`)
      .then(r => setRows(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loading />;

  const byTier  = Object.fromEntries(rows.map(r => [r.tier, parseInt(r.count)]));
  const total   = TIER_ORDER.reduce((s, t) => s + (byTier[t] || 0), 0);
  const maxCount = Math.max(1, ...TIER_ORDER.map(t => byTier[t] || 0));

  const familyTotals = {};
  for (const tier of TIER_ORDER) {
    const f = TIER_FAMILY[tier];
    familyTotals[f] = (familyTotals[f] || 0) + (byTier[tier] || 0);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Family summary */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
        {FAMILIES.map(f => {
          const cnt = familyTotals[f] || 0;
          const color = RANK_COLORS[f];
          return (
            <div key={f} style={{
              background: 'var(--cb-white)',
              border: `1px solid var(--cb-gray-200)`,
              borderTop: `3px solid ${color}`,
              borderRadius: 'var(--radius-lg)',
              boxShadow: 'var(--shadow-sm)',
              padding: '14px 18px',
              minWidth: 100,
              flex: '1 1 100px',
              textAlign: 'center',
            }}>
              <div style={{ fontSize: 22, fontWeight: 800, color }}>{fmt(cnt)}</div>
              <div style={{ fontSize: 11, color: 'var(--cb-gray-500)', marginTop: 3, fontWeight: 500 }}>{f}</div>
              <div style={{ fontSize: 11, color: 'var(--cb-gray-500)', marginTop: 2 }}>
                {total > 0 ? `${((cnt / total) * 100).toFixed(1)}%` : '0%'}
              </div>
            </div>
          );
        })}
      </div>

      {/* Tier breakdown */}
      <SectionCard title={`All Tiers — ${fmt(total)} ranked players`}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '130px 1fr 64px 64px',
          gap: '8px 16px',
          alignItems: 'center',
        }}>
          {/* Column headers */}
          {['Tier','Distribution','Players','Share'].map((h, i) => (
            <div key={h} style={{
              fontSize: 11,
              fontWeight: 600,
              color: 'var(--cb-gray-500)',
              textTransform: 'uppercase',
              letterSpacing: '.05em',
              textAlign: i >= 2 ? 'right' : 'left',
              paddingBottom: 6,
              borderBottom: '1px solid var(--cb-gray-200)',
            }}>{h}</div>
          ))}

          {TIER_ORDER.map(tier => {
            const cnt   = byTier[tier] || 0;
            const color = RANK_COLORS[TIER_FAMILY[tier]];
            const share = total > 0 ? ((cnt / total) * 100).toFixed(1) : '0.0';
            return (
              <React.Fragment key={tier}>
                <div style={{
                  fontSize: 13,
                  color: 'var(--cb-gray-900)',
                  fontWeight: tier === 'Grandmaster' ? 700 : 400,
                }}>
                  {tier}
                </div>
                <Bar value={cnt} max={maxCount} color={color} />
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--cb-navy-text)', textAlign: 'right' }}>
                  {fmt(cnt)}
                </div>
                <div style={{ fontSize: 12, color: 'var(--cb-gray-500)', textAlign: 'right' }}>
                  {share}%
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </SectionCard>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB: BOT DISTRIBUTION
// ═══════════════════════════════════════════════════════════════════════════════
function BotDistributionTab() {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/api/admin/bot-distribution`)
      .then(r => setRows(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loading />;

  const byTier = Object.fromEntries(
    rows.map(r => [r.tier, { bots: parseInt(r.bot_count), players: parseInt(r.player_count), total: parseInt(r.total_count) }])
  );

  const totalBots    = rows.reduce((s, r) => s + parseInt(r.bot_count),    0);
  const totalPlayers = rows.reduce((s, r) => s + parseInt(r.player_count), 0);
  const totalAll     = totalBots + totalPlayers;
  const maxBots      = Math.max(1, ...TIER_ORDER.map(t => byTier[t]?.bots || 0));

  // Family rollups
  const familyBots = {};
  for (const tier of TIER_ORDER) {
    const f = TIER_FAMILY[tier];
    familyBots[f] = (familyBots[f] || 0) + (byTier[tier]?.bots || 0);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Summary cards */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
        <StatCard label="Total Bots"         value={totalBots}    accent="var(--cb-blue)" />
        <StatCard label="Total Real Players" value={totalPlayers} />
        <StatCard
          label="Bot Ratio"
          value={totalAll > 0 ? `${((totalBots / totalAll) * 100).toFixed(1)}%` : '—'}
          sub="Bots as % of all accounts"
          accent="var(--cb-gray-500)"
        />
      </div>

      {/* Bot counts per rank family */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
        {FAMILIES.map(f => {
          const bots = familyBots[f] || 0;
          const color = RANK_COLORS[f];
          return (
            <div key={f} style={{
              background: 'var(--cb-white)',
              border: '1px solid var(--cb-gray-200)',
              borderTop: `3px solid ${color}`,
              borderRadius: 'var(--radius-lg)',
              boxShadow: 'var(--shadow-sm)',
              padding: '14px 18px',
              minWidth: 100,
              flex: '1 1 100px',
              textAlign: 'center',
            }}>
              <div style={{ fontSize: 22, fontWeight: 800, color }}>{fmt(bots)}</div>
              <div style={{ fontSize: 11, color: 'var(--cb-gray-500)', marginTop: 3, fontWeight: 500 }}>{f}</div>
              <div style={{ fontSize: 11, color: 'var(--cb-gray-500)', marginTop: 2 }}>
                {totalBots > 0 ? `${((bots / totalBots) * 100).toFixed(1)}% of bots` : '—'}
              </div>
            </div>
          );
        })}
      </div>

      {/* Per-tier breakdown: bots vs players side by side */}
      <SectionCard title="Bot vs Player Distribution — Per Tier">
        <div style={{
          display: 'grid',
          gridTemplateColumns: '130px 1fr 56px 1fr 56px 68px',
          gap: '8px 14px',
          alignItems: 'center',
        }}>
          {/* Headers */}
          {['Tier', 'Bots', 'Count', 'Players', 'Count', 'Bot %'].map((h, i) => (
            <div key={i} style={{
              fontSize: 11,
              fontWeight: 600,
              color: 'var(--cb-gray-500)',
              textTransform: 'uppercase',
              letterSpacing: '.05em',
              textAlign: i >= 2 ? 'right' : 'left',
              paddingBottom: 6,
              borderBottom: '1px solid var(--cb-gray-200)',
            }}>{h}</div>
          ))}

          {TIER_ORDER.map(tier => {
            const d     = byTier[tier] || { bots: 0, players: 0, total: 0 };
            const color = RANK_COLORS[TIER_FAMILY[tier]];
            const botPct = d.total > 0 ? ((d.bots / d.total) * 100).toFixed(0) : '0';
            const maxPlayers = Math.max(1, ...TIER_ORDER.map(t => byTier[t]?.players || 0));
            return (
              <React.Fragment key={tier}>
                <div style={{
                  fontSize: 13,
                  color: 'var(--cb-gray-900)',
                  fontWeight: tier === 'Grandmaster' ? 700 : 400,
                }}>
                  {tier}
                </div>
                {/* Bot bar */}
                <Bar value={d.bots} max={maxBots} color={color} />
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--cb-navy-text)', textAlign: 'right' }}>
                  {fmt(d.bots)}
                </div>
                {/* Player bar */}
                <Bar value={d.players} max={maxPlayers} color="var(--cb-gray-300)" />
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--cb-navy-text)', textAlign: 'right' }}>
                  {fmt(d.players)}
                </div>
                {/* Bot % */}
                <div style={{
                  fontSize: 12,
                  textAlign: 'right',
                  color: parseInt(botPct) > 50 ? 'var(--cb-red)' : 'var(--cb-gray-500)',
                  fontWeight: parseInt(botPct) > 50 ? 700 : 400,
                }}>
                  {botPct}%
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </SectionCard>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB: PLAYER MANAGER
// ═══════════════════════════════════════════════════════════════════════════════
function PlayerManagerTab() {
  const [data, setData]         = useState({ players: [], total: 0 });
  const [loading, setLoading]   = useState(true);
  const [page, setPage]         = useState(1);
  const [search, setSearch]     = useState('');
  const [includeBot, setIncBot] = useState(false);
  const [sortBy, setSortBy]     = useState('rating');
  const [sortDir, setSortDir]   = useState('DESC');
  const [editing, setEditing]   = useState(null);
  const [editVals, setEditVals] = useState({});
  const [saving, setSaving]     = useState(false);
  const [msg, setMsg]           = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/api/admin/players`, {
      params: { page, limit: 25, search, includeBot, sortBy, sortDir },
    }).then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [page, search, includeBot, sortBy, sortDir]);

  useEffect(() => { load(); }, [load]);

  const openEdit = p => {
    setEditing(p);
    setEditVals({ rating: p.rating, wins: p.wins, losses: p.losses, is_admin: p.is_admin });
    setMsg(null);
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/api/admin/players/${editing.id}`, editVals);
      setEditing(null);
      load();
    } catch (e) {
      setMsg(e.response?.data?.error || 'Error saving.');
    }
    setSaving(false);
  };

  const deletePlayer = async p => {
    if (!window.confirm(`Delete "${p.display_name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/api/admin/players/${p.id}`);
      load();
    } catch (e) {
      alert(e.response?.data?.error || 'Error deleting.');
    }
  };

  const cols = [
    { key: 'display_name', label: 'Name', render: (v, r) => (
      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontWeight: 600, color: 'var(--cb-navy-text)' }}>{v}</span>
        {r.is_admin && (
          <span style={{ fontSize: 10, background: 'var(--cb-red-light)', color: 'var(--cb-red)', border: '1px solid var(--cb-red)', borderRadius: 3, padding: '1px 5px', fontWeight: 700 }}>
            ADMIN
          </span>
        )}
        {r.is_bot && (
          <span style={{ fontSize: 10, background: 'var(--cb-gray-200)', color: 'var(--cb-gray-500)', borderRadius: 3, padding: '1px 5px', fontWeight: 600 }}>
            BOT
          </span>
        )}
      </span>
    )},
    { key: 'user_tag',     label: 'Tag',    render: v => <code style={{ fontSize: 11, color: 'var(--cb-gray-700)' }}>{v}</code> },
    { key: 'rating',       label: 'Rating', right: true, render: (v) => (
      <span style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end' }}>
        <span style={{ fontWeight: 700 }}>{fmt(v)}</span>
        <RankBadge rating={v} />
      </span>
    )},
    { key: 'wins',         label: 'W',      right: true, render: v => <span style={{ color: 'var(--cb-green)', fontWeight: 600 }}>{v}</span> },
    { key: 'losses',       label: 'L',      right: true, render: v => <span style={{ color: 'var(--cb-red)', fontWeight: 600 }}>{v}</span> },
    { key: 'school_state', label: 'State' },
    { key: 'login_streak', label: 'Streak', right: true },
    { key: 'created_at',   label: 'Joined', render: v => <span style={{ color: 'var(--cb-gray-500)', fontSize: 12 }}>{ago(v)}</span> },
    { key: '_actions',     label: '',       render: (_, r) => (
      <span style={{ display: 'flex', gap: 6 }}>
        <button className="btn btn-ghost btn-sm" style={{ fontSize: 11, padding: '4px 10px' }} onClick={() => openEdit(r)}>Edit</button>
        <button className="btn btn-ghost btn-sm" style={{ fontSize: 11, padding: '4px 10px', color: 'var(--cb-red)' }} onClick={() => deletePlayer(r)}>Delete</button>
      </span>
    )},
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Controls */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          className="form-input"
          placeholder="Search name, email, tag…"
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
          style={{ flex: 1, minWidth: 200, maxWidth: 320 }}
        />
        <select
          className="form-input"
          value={sortBy}
          onChange={e => setSortBy(e.target.value)}
          style={{ width: 160 }}
        >
          <option value="rating">Sort: Rating</option>
          <option value="wins">Sort: Wins</option>
          <option value="losses">Sort: Losses</option>
          <option value="created_at">Sort: Joined</option>
          <option value="login_streak">Sort: Streak</option>
        </select>
        <select
          className="form-input"
          value={sortDir}
          onChange={e => setSortDir(e.target.value)}
          style={{ width: 120 }}
        >
          <option value="DESC">Descending</option>
          <option value="ASC">Ascending</option>
        </select>
        <label style={{ display: 'flex', gap: 7, alignItems: 'center', fontSize: 13, color: 'var(--cb-gray-700)', cursor: 'pointer', userSelect: 'none' }}>
          <input type="checkbox" checked={includeBot} onChange={e => setIncBot(e.target.checked)} />
          Include bots
        </label>
      </div>

      <SectionCard>
        {loading ? <Loading /> : <Table cols={cols} rows={data.players} />}
        <Pagination page={page} total={data.total} limit={25} onPage={setPage} />
      </SectionCard>

      {/* Edit modal */}
      {editing && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => setEditing(null)}
        >
          <div
            style={{ background: 'var(--cb-white)', border: '1px solid var(--cb-gray-200)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)', padding: 32, width: 380, maxWidth: '90vw' }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--cb-navy-text)', marginBottom: 20 }}>
              Edit — {editing.display_name}
            </div>
            {[
              { key: 'rating', label: 'Rating' },
              { key: 'wins',   label: 'Wins'   },
              { key: 'losses', label: 'Losses' },
            ].map(f => (
              <div key={f.key} style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--cb-gray-700)', marginBottom: 5 }}>{f.label}</label>
                <input
                  className="form-input"
                  type="number"
                  value={editVals[f.key] ?? ''}
                  onChange={e => setEditVals(v => ({ ...v, [f.key]: parseInt(e.target.value) || 0 }))}
                />
              </div>
            ))}
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, color: 'var(--cb-gray-700)', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={editVals.is_admin || false}
                  onChange={e => setEditVals(v => ({ ...v, is_admin: e.target.checked }))}
                />
                Grant admin access
              </label>
            </div>
            {msg && <div style={{ fontSize: 13, color: 'var(--cb-red)', marginBottom: 12 }}>{msg}</div>}
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-primary btn-sm" onClick={saveEdit} disabled={saving}>
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
              <button className="btn btn-ghost btn-sm" onClick={() => setEditing(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB: MATCH HISTORY
// ═══════════════════════════════════════════════════════════════════════════════
const REASON_COLORS = {
  completed: 'var(--cb-green)',
  timeout:   'var(--cb-yellow)',
  resign:    'var(--cb-gray-500)',
  forfeit:   'var(--cb-red)',
};

function MatchHistoryTab() {
  const [data, setData]       = useState({ matches: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [page, setPage]       = useState(1);
  const [search, setSearch]   = useState('');
  const [reason, setReason]   = useState('');

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/api/admin/matches`, { params: { page, limit: 25, search, reason } })
      .then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [page, search, reason]);

  useEffect(() => { load(); }, [load]);

  const cols = [
    { key: 'created_at',     label: 'When',    render: v => <span style={{ color: 'var(--cb-gray-500)', fontSize: 12 }}>{ago(v)}</span> },
    { key: 'p1_name',        label: 'Player 1', render: (v, r) => (
      <span style={{ fontWeight: r.winner_id === r.player1_id ? 700 : 400, color: r.winner_id === r.player1_id ? 'var(--cb-green)' : 'inherit' }}>
        {v}{r.p1_is_bot ? ' (bot)' : ''}
      </span>
    )},
    { key: 'player1_score',  label: 'Score',    right: true, render: (v, r) => (
      <span style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{v} – {r.player2_score}</span>
    )},
    { key: 'p2_name',        label: 'Player 2', render: (v, r) => (
      <span style={{ fontWeight: r.winner_id === r.player2_id ? 700 : 400, color: r.winner_id === r.player2_id ? 'var(--cb-green)' : 'inherit' }}>
        {v}{r.p2_is_bot ? ' (bot)' : ''}
      </span>
    )},
    { key: 'reason',         label: 'Reason',   render: v => (
      <span style={{ color: REASON_COLORS[v] || 'var(--cb-gray-500)', fontSize: 12, fontWeight: 600, textTransform: 'capitalize' }}>{v}</span>
    )},
    { key: 'rating_delta_p1', label: 'P1', right: true, render: v => (
      <span style={{ color: v > 0 ? 'var(--cb-green)' : v < 0 ? 'var(--cb-red)' : 'var(--cb-gray-500)', fontWeight: 600, fontSize: 12 }}>
        {v > 0 ? `+${v}` : v}
      </span>
    )},
    { key: 'rating_delta_p2', label: 'P2', right: true, render: v => (
      <span style={{ color: v > 0 ? 'var(--cb-green)' : v < 0 ? 'var(--cb-red)' : 'var(--cb-gray-500)', fontWeight: 600, fontSize: 12 }}>
        {v > 0 ? `+${v}` : v}
      </span>
    )},
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <input
          className="form-input"
          placeholder="Search player name…"
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
          style={{ flex: 1, minWidth: 200, maxWidth: 320 }}
        />
        <select
          className="form-input"
          value={reason}
          onChange={e => { setReason(e.target.value); setPage(1); }}
          style={{ width: 160 }}
        >
          <option value="">All reasons</option>
          <option value="completed">Completed</option>
          <option value="timeout">Timeout</option>
          <option value="resign">Resign</option>
          <option value="forfeit">Forfeit</option>
        </select>
      </div>
      <SectionCard>
        {loading ? <Loading /> : <Table cols={cols} rows={data.matches} />}
        <Pagination page={page} total={data.total} limit={25} onPage={setPage} />
      </SectionCard>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB: QUESTION ANALYTICS
// ═══════════════════════════════════════════════════════════════════════════════
function QuestionAnalyticsTab() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [subtab, setSubtab]   = useState('domain');

  useEffect(() => {
    axios.get(`${API}/api/admin/question-analytics`)
      .then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <Loading />;
  if (!data) return <div style={{ padding: 32, color: 'var(--cb-gray-500)' }}>No data.</div>;

  function accuracyColor(v) {
    const n = parseFloat(v);
    if (n >= 70) return 'var(--cb-green)';
    if (n >= 50) return 'var(--cb-yellow)';
    return 'var(--cb-red)';
  }

  const domainCols = [
    { key: 'section',       label: 'Section' },
    { key: 'domain',        label: 'Domain' },
    { key: 'total_answers', label: 'Answers', right: true, render: v => fmt(v) },
    { key: 'accuracy_pct',  label: 'Accuracy', right: true, render: v => (
      <span style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
        <div style={{ width: 56 }}>
          <Bar value={parseFloat(v) || 0} max={100} color={accuracyColor(v)} />
        </div>
        <span style={{ color: accuracyColor(v), fontWeight: 700, minWidth: 36, textAlign: 'right' }}>{pct(v)}</span>
      </span>
    )},
  ];

  const skillCols = [
    { key: 'section',       label: 'Section' },
    { key: 'domain',        label: 'Domain' },
    { key: 'skill',         label: 'Skill' },
    { key: 'total_answers', label: 'Answers', right: true, render: v => fmt(v) },
    { key: 'accuracy_pct',  label: 'Accuracy', right: true, render: v => (
      <span style={{ color: accuracyColor(v), fontWeight: 700 }}>{pct(v)}</span>
    )},
  ];

  const missedCols = [
    { key: 'question_id',    label: 'Question ID' },
    { key: 'section',        label: 'Section' },
    { key: 'domain',         label: 'Domain' },
    { key: 'skill',          label: 'Skill' },
    { key: 'total_wrong',    label: 'Times Missed',   right: true, render: v => <strong>{fmt(v)}</strong> },
    { key: 'unique_players', label: 'Unique Players', right: true, render: v => fmt(v) },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', gap: 6 }}>
        {[['domain','By Domain'],['skill','By Skill'],['missed','Most Missed']].map(([k, l]) => (
          <button
            key={k}
            className={`btn btn-sm ${subtab === k ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setSubtab(k)}
          >
            {l}
          </button>
        ))}
      </div>
      <SectionCard>
        {subtab === 'domain' && <Table cols={domainCols} rows={data.byDomain}   keyField="domain" />}
        {subtab === 'skill'  && <Table cols={skillCols}  rows={data.bySkill}    keyField="skill" />}
        {subtab === 'missed' && <Table cols={missedCols} rows={data.mostMissed} keyField="question_id" />}
      </SectionCard>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB: GEOGRAPHIC
// ═══════════════════════════════════════════════════════════════════════════════
function GeographicTab() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/api/admin/geographic`)
      .then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <Loading />;
  if (!data)   return <div style={{ padding: 32, color: 'var(--cb-gray-500)' }}>No data.</div>;

  const maxState = Math.max(1, ...data.byState.map(r => parseInt(r.count)));

  const stateCols = [
    { key: 'state', label: 'State' },
    { key: 'count', label: 'Players', right: true, render: (v) => (
      <span style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'flex-end' }}>
        <div style={{ width: 80 }}><Bar value={parseInt(v)} max={maxState} color="var(--cb-blue)" /></div>
        <span style={{ fontWeight: 700, minWidth: 32, textAlign: 'right' }}>{fmt(v)}</span>
      </span>
    )},
  ];

  const schoolCols = [
    { key: 'school',       label: 'School' },
    { key: 'school_state', label: 'State' },
    { key: 'school_city',  label: 'City' },
    { key: 'count',        label: 'Players',    right: true, render: v => fmt(v) },
    { key: 'avg_rating',   label: 'Avg Rating', right: true, render: v => fmt(v) },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
        <StatCard label="States Represented" value={data.byState.length} accent="var(--cb-blue)" />
        <StatCard label="Top Schools Tracked" value={data.topSchools.length} accent="var(--cb-green)" />
        <StatCard label="No School Set" value={data.noSchool} sub="Players without a school" accent="var(--cb-yellow)" />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        <SectionCard title="Players by State">
          <Table cols={stateCols} rows={data.byState} keyField="state" />
        </SectionCard>
        <SectionCard title="Top 50 Schools">
          <Table cols={schoolCols} rows={data.topSchools} keyField="school" />
        </SectionCard>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB: SUSPICIOUS ACTIVITY
// ═══════════════════════════════════════════════════════════════════════════════
function SuspiciousTab() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/api/admin/suspicious`)
      .then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <Loading />;
  if (!data)   return <div style={{ padding: 32, color: 'var(--cb-gray-500)' }}>No data.</div>;

  const winRateCols = [
    { key: 'display_name', label: 'Player',   render: v => <span style={{ fontWeight: 600 }}>{v}</span> },
    { key: 'user_tag',     label: 'Tag',      render: v => <code style={{ fontSize: 11, color: 'var(--cb-gray-700)' }}>{v}</code> },
    { key: 'rating',       label: 'Rating',   right: true, render: v => <span style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', alignItems: 'center' }}><span style={{ fontWeight: 700 }}>{fmt(v)}</span><RankBadge rating={v} /></span> },
    { key: 'wins',         label: 'W',        right: true },
    { key: 'losses',       label: 'L',        right: true },
    { key: 'win_rate_pct', label: 'Win Rate', right: true, render: v => (
      <span style={{ fontWeight: 700, color: parseFloat(v) >= 90 ? 'var(--cb-red)' : 'var(--cb-yellow)' }}>{pct(v)}</span>
    )},
    { key: 'created_at',   label: 'Joined',   render: v => <span style={{ color: 'var(--cb-gray-500)', fontSize: 12 }}>{ago(v)}</span> },
  ];

  const forfeitCols = [
    { key: 'display_name',   label: 'Player',       render: v => <span style={{ fontWeight: 600 }}>{v}</span> },
    { key: 'user_tag',       label: 'Tag',          render: v => <code style={{ fontSize: 11, color: 'var(--cb-gray-700)' }}>{v}</code> },
    { key: 'rating',         label: 'Rating',       right: true, render: v => <span style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', alignItems: 'center' }}><span style={{ fontWeight: 700 }}>{fmt(v)}</span><RankBadge rating={v} /></span> },
    { key: 'total_forfeits', label: 'Forfeit Wins', right: true, render: v => <span style={{ fontWeight: 700, color: 'var(--cb-red)' }}>{fmt(v)}</span> },
  ];

  const newHighCols = [
    { key: 'display_name', label: 'Player', render: v => <span style={{ fontWeight: 600 }}>{v}</span> },
    { key: 'user_tag',     label: 'Tag',    render: v => <code style={{ fontSize: 11, color: 'var(--cb-gray-700)' }}>{v}</code> },
    { key: 'rating',       label: 'Rating', right: true, render: v => <span style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', alignItems: 'center' }}><span style={{ fontWeight: 700 }}>{fmt(v)}</span><RankBadge rating={v} /></span> },
    { key: 'wins',         label: 'W',      right: true },
    { key: 'losses',       label: 'L',      right: true },
    { key: 'created_at',   label: 'Joined', render: v => <span style={{ color: 'var(--cb-gray-500)', fontSize: 12 }}>{ago(v)}</span> },
  ];

  const FlagSection = ({ title, desc, borderColor, rows, cols }) => (
    <div style={{
      background: 'var(--cb-white)',
      border: '1px solid var(--cb-gray-200)',
      borderLeft: `4px solid ${borderColor}`,
      borderRadius: 'var(--radius-lg)',
      boxShadow: 'var(--shadow-sm)',
      overflow: 'hidden',
      marginBottom: 16,
    }}>
      <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--cb-gray-200)' }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--cb-navy-text)' }}>{title}</div>
        <div style={{ fontSize: 12, color: 'var(--cb-gray-500)', marginTop: 3 }}>{desc}</div>
      </div>
      <div style={{ padding: '0' }}>
        <Table cols={cols} rows={rows} />
      </div>
    </div>
  );

  return (
    <div>
      <div style={{
        background: 'var(--cb-yellow-light)',
        border: '1px solid var(--cb-yellow)',
        borderRadius: 'var(--radius)',
        padding: '10px 16px',
        marginBottom: 20,
        fontSize: 13,
        color: 'var(--cb-gray-700)',
      }}>
        These flags are heuristic and may include legitimate players. Use them as starting points for investigation, not automatic bans.
      </div>
      <FlagSection
        title="Unusually High Win Rate"
        desc="Players with a win rate of 80% or higher across 10+ games."
        borderColor="var(--cb-yellow)"
        rows={data.highWinRate}
        cols={winRateCols}
      />
      <FlagSection
        title="High Forfeit Win Count"
        desc="Players who have won 5 or more games through opponent disconnects. May indicate disconnect farming."
        borderColor="var(--cb-red)"
        rows={data.highForfeit}
        cols={forfeitCols}
      />
      <FlagSection
        title="New Accounts with High Ratings"
        desc="Accounts created in the last 7 days with 1,500+ XP (Silver I or above). May indicate smurf accounts."
        borderColor="var(--cb-blue)"
        rows={data.newHighRated}
        cols={newHighCols}
      />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB: SYSTEM SETTINGS
// ═══════════════════════════════════════════════════════════════════════════════
function SystemSettingsTab() {
  const [settings, setSettings] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [newKey, setNewKey]     = useState('');
  const [newVal, setNewVal]     = useState('');
  const [saving, setSaving]     = useState(null);
  const [editKey, setEditKey]   = useState(null);
  const [editVal, setEditVal]   = useState('');

  const load = () => {
    setLoading(true);
    axios.get(`${API}/api/admin/system-settings`)
      .then(r => setSettings(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const save = async (key, value) => {
    setSaving(key);
    try {
      await axios.put(`${API}/api/admin/system-settings/${key}`, { value });
      load();
      setEditKey(null);
    } catch { /* ignore */ }
    setSaving(null);
  };

  const del = async key => {
    if (!window.confirm(`Delete setting "${key}"?`)) return;
    await axios.delete(`${API}/api/admin/system-settings/${key}`).catch(() => {});
    load();
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <SectionCard title="Add / Update Setting">
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <input
            className="form-input"
            placeholder="key"
            value={newKey}
            onChange={e => setNewKey(e.target.value)}
            style={{ flex: '1 1 140px' }}
          />
          <input
            className="form-input"
            placeholder="value"
            value={newVal}
            onChange={e => setNewVal(e.target.value)}
            style={{ flex: '2 1 220px' }}
          />
          <button
            className="btn btn-primary btn-sm"
            disabled={!newKey.trim() || !!saving}
            onClick={async () => {
              await save(newKey.trim(), newVal);
              setNewKey('');
              setNewVal('');
            }}
          >
            Save
          </button>
        </div>
      </SectionCard>

      <SectionCard title={`Current Settings (${settings.length})`}>
        {loading ? <Loading /> : settings.length === 0 ? (
          <div style={{ color: 'var(--cb-gray-500)', fontSize: 13 }}>No settings stored.</div>
        ) : settings.map(s => (
          <div
            key={s.key}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '10px 0',
              borderBottom: '1px solid var(--cb-gray-200)',
              flexWrap: 'wrap',
            }}
          >
            <code style={{ flex: '0 0 200px', fontSize: 12, color: 'var(--cb-blue)', fontWeight: 600 }}>
              {s.key}
            </code>
            {editKey === s.key ? (
              <>
                <input
                  className="form-input"
                  value={editVal}
                  onChange={e => setEditVal(e.target.value)}
                  style={{ flex: 1, minWidth: 120 }}
                />
                <button className="btn btn-primary btn-sm" onClick={() => save(s.key, editVal)} disabled={saving === s.key}>
                  {saving === s.key ? '…' : 'Save'}
                </button>
                <button className="btn btn-ghost btn-sm" onClick={() => setEditKey(null)}>Cancel</button>
              </>
            ) : (
              <>
                <span style={{ flex: 1, fontSize: 13, color: 'var(--cb-gray-900)', wordBreak: 'break-all' }}>{s.value}</span>
                <span style={{ fontSize: 11, color: 'var(--cb-gray-500)' }}>{ago(s.updated_at)}</span>
                <button className="btn btn-ghost btn-sm" style={{ fontSize: 11 }} onClick={() => { setEditKey(s.key); setEditVal(s.value); }}>Edit</button>
                <button className="btn btn-ghost btn-sm" style={{ fontSize: 11, color: 'var(--cb-red)' }} onClick={() => del(s.key)}>Delete</button>
              </>
            )}
          </div>
        ))}
      </SectionCard>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN ADMIN PAGE
// ═══════════════════════════════════════════════════════════════════════════════
const TABS = [
  { id: 'overview',   label: 'Overview'            },
  { id: 'ranks',      label: 'Rank Distribution'   },
  { id: 'bots',       label: 'Bot Distribution'    },
  { id: 'players',    label: 'Player Manager'      },
  { id: 'matches',    label: 'Match History'       },
  { id: 'questions',  label: 'Question Analytics'  },
  { id: 'geo',        label: 'Geographic'          },
  { id: 'suspicious', label: 'Suspicious Activity' },
  { id: 'settings',   label: 'System Settings'     },
];

export default function Admin() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');

  if (!user?.is_admin) {
    return (
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', height: '60vh', gap: 12,
      }}>
        <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--cb-navy-text)' }}>Access Denied</div>
        <div style={{ color: 'var(--cb-gray-500)', fontSize: 14 }}>You do not have admin privileges.</div>
      </div>
    );
  }

  return (
    <div className="page-wide" style={{ paddingTop: 32, paddingBottom: 48 }}>
      {/* Page header */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 26, fontWeight: 800, color: 'var(--cb-navy-text)', letterSpacing: '-.02em', margin: 0 }}>
          Admin Console
        </h1>
        <div style={{ fontSize: 13, color: 'var(--cb-gray-500)', marginTop: 5 }}>
          Signed in as <strong style={{ color: 'var(--cb-gray-700)' }}>{user.display_name || user.name}</strong>
        </div>
      </div>

      {/* Tab bar */}
      <div style={{
        display: 'flex',
        gap: 2,
        flexWrap: 'wrap',
        borderBottom: '2px solid var(--cb-gray-200)',
        marginBottom: 24,
      }}>
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            style={{
              padding: '9px 16px',
              fontSize: 13,
              fontWeight: 600,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: activeTab === t.id ? 'var(--cb-navy-text)' : 'var(--cb-gray-500)',
              borderBottom: activeTab === t.id ? '2px solid var(--cb-navy)' : '2px solid transparent',
              marginBottom: -2,
              transition: 'color .15s',
              fontFamily: 'inherit',
              whiteSpace: 'nowrap',
            }}
            onMouseEnter={e => { if (activeTab !== t.id) e.currentTarget.style.color = 'var(--cb-gray-900)'; }}
            onMouseLeave={e => { if (activeTab !== t.id) e.currentTarget.style.color = 'var(--cb-gray-500)'; }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'overview'   && <OverviewTab />}
      {activeTab === 'ranks'      && <RankDistributionTab />}
      {activeTab === 'bots'       && <BotDistributionTab />}
      {activeTab === 'players'    && <PlayerManagerTab />}
      {activeTab === 'matches'    && <MatchHistoryTab />}
      {activeTab === 'questions'  && <QuestionAnalyticsTab />}
      {activeTab === 'geo'        && <GeographicTab />}
      {activeTab === 'suspicious' && <SuspiciousTab />}
      {activeTab === 'settings'   && <SystemSettingsTab />}
    </div>
  );
}
